<template><div><code-well-header>
  <a href="#link" class="d-link">Base link</a>
</code-well-header>
<!-- <component-combinator component-name="DtLink" /> -->
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph"><code v-pre>button</code> and link (<code v-pre>&lt;a&gt;</code>) HTML elements each describe a specific intent. Understanding the distinction is important: if it goes somewhere, use a Link. If an action occurs, use a <RouteLink to="/components/button.html" class="d-docsite--link d-link">Button</RouteLink>.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use for navigating between destinations.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use for actions, instead use a <RouteLink to="/components/button.html" class="d-docsite--link d-link">Button</RouteLink>.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Use useful, actionable, and descriptive text clearly conveying the hyperlink’s destination. For example, a generic label like &quot;click here&quot; doesn’t convey its target content.</li>
<li class="d-docsite--list-element">Too many links can be overwhelming. Be selective about the number of links in a context.</li>
<li class="d-docsite--list-element">Clearly identify links that target an external source.</li>
</ul>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Allow keyboard navigation. Users must be able to navigate between links, i.e. keypress of tab, and activate it by pressing ‘Enter’.</li>
<li class="d-docsite--list-element">Users must be able to identify links without relying on color alone.</li>
<li class="d-docsite--list-element">Users must be able to activate hover and focus states with both a mouse and a keyboard.</li>
</ul>
<h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-well-header>
  <a href="#link" class="d-link">Base link</a>
  <a href="#link" class="d-link d-link--danger">Danger link</a>
  <a href="#link" class="d-link d-link--muted">Muted link</a>
  <a href="#link" class="d-link d-link--success">Success link</a>
  <a href="#link" class="d-link d-link--warning">Warning link</a>
  <a href="#link" class="d-link d-link--mention">Mention link</a>
</code-well-header>
<code-example-tabs
htmlCode='
<a href="#link" class="d-link">Link</a>
<a href="#link" class="d-link d-link--danger">Danger link</a>
<a href="#link" class="d-link d-link--muted">Muted link</a>
<a href="#link" class="d-link d-link--success">Success link</a>
<a href="#link" class="d-link d-link--warning">Warning link</a>
<a href="#link" class="d-link d-link--mention">Mention link</a>
'
vueCode='
<dt-link :href="#link">Link</dt-link>
<dt-link :href="#link" kind="danger">Danger link</dt-link>
<dt-link :href="#link" kind="muted">Muted link</dt-link>
<dt-link :href="#link" kind="success">Success link</dt-link>
<dt-link :href="#link" kind="warning">Warning link</dt-link>
<dt-link :href="#link" kind="mention">Mention link</dt-link>
'
showHtmlWarning />
<h3 id="inverted" tabindex="-1" class="d-docsite--header-3">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h3>
<code-well-header bgclass="d-bgc-contrast">
  <a href="#link" class="d-link d-link--inverted">Inverted base link</a>
  <a href="#link" class="d-link d-link--inverted-danger">Inverted danger link</a>
  <a href="#link" class="d-link d-link--inverted-success">Inverted success link</a>
  <a href="#link" class="d-link d-link--inverted-warning">Inverted warning link</a>
  <a href="#link" class="d-link d-link--inverted-muted">Inverted muted link</a>
  <a href="#link" class="d-link d-link--inverted-mention">Inverted mention link</a>
</code-well-header>
<code-example-tabs
htmlCode='
<a href="#link" class="d-link d-link--inverted">Inverted link</a>
<a href="#link" class="d-link d-link--inverted-danger">Inverted danger link</a>
<a href="#link" class="d-link d-link--inverted-success">Inverted success link</a>
<a href="#link" class="d-link d-link--inverted-warning">Inverted warning link</a>
<a href="#link" class="d-link d-link--inverted-muted">Inverted muted link</a>
<a href="#link" class="d-link d-link--inverted-mention">Inverted muted link</a>
'
vueCode='
<dt-link :href="#link" inverted>Inverted link</dt-link>
<dt-link :href="#link" kind="danger" inverted>Inverted danger link</dt-link>
<dt-link :href="#link" kind="success" inverted>Inverted success link</dt-link>
<dt-link :href="#link" kind="warning" inverted>Inverted warning link</dt-link>
<dt-link :href="#link" kind="muted" inverted>Inverted muted link</dt-link>
<dt-link :href="#link" kind="mention" inverted>Inverted mention link</dt-link>
'
showHtmlWarning />
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="link" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="link"></component-class-table></div></template>


